import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import {
  ArrowUpToLineIcon,
  Plus,
  Download,
  Trash2,
  ExternalLink,
  Edit,
  Heart,
  Shield,
  Syringe,
  Microchip,
} from 'lucide-react';
import { toast } from 'sonner';

import { fetchOwnedPets, deleteOwnedPet, downloadQRCode } from '@/api/ownedPets';
import { useAuthToken } from '@/hooks/useAuthToken';
import { LostAndFoundSkeleton } from '@/components/Skeletons';
import { ANIMAL_TYPES, ANIMAL_AGES, PET_COLORS, PET_GENDER } from '@/constants';
import { NbButton, NbCard, NbChip, NbPageHeader } from '@/components/Neobrut';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const MyPets = () => {
  const { token, isTokenExpired } = useAuthToken();
  const navigate = useNavigate();
  const titleRef = useRef<HTMLDivElement | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [fileType, setFileType] = useState<'png' | 'svg'>('png');

  const {
    data: pets,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['owned-pets'],
    queryFn: async (): Promise<OwnedPet[]> => {
      if (!token || isTokenExpired(token)) {
        toast.info('Tu sesión ha expirado. Iniciá sesión nuevamente.');
        navigate('/login');
        return [];
      }
      return fetchOwnedPets(token);
    },
  });

  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      setShowScrollButton(e.touches[0].clientY < window.innerHeight * 0.8);
    };
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => window.removeEventListener('touchmove', handleTouchMove);
  }, []);

  const scrollToTop = () =>
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

  const handleDownloadQR = async (pet: OwnedPet, format: 'png' | 'svg') => {
    if (!token) return;
    setFileType(format);
    try {
      const blob = await downloadQRCode(pet.id, token, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pet.name}_qr_code.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Código QR descargado');
    } catch (error) {
      toast.error('Error al descargar el código QR');
      console.error(error);
    }
  };

  const handleDeletePet = async (petId: number, petName: string) => {
    if (!token) return;
    if (!confirm(`¿Estás seguro de que querés eliminar el perfil de ${petName}?`)) return;
    try {
      await deleteOwnedPet(petId, token);
      toast.success('Perfil eliminado');
      refetch();
    } catch (error) {
      toast.error('Error al eliminar el perfil');
      console.error(error);
    }
  };

  const handleOpenPublicProfile = (uuid: string) => {
    window.open(`${window.location.origin}/pet-profile/${uuid}`, '_blank');
  };

  if (isLoading) return <LostAndFoundSkeleton />;

  return (
    <div className="mx-auto w-full max-w-4xl lg:max-w-5xl pb-10">
      <div ref={titleRef}>
        <NbPageHeader
          slug="/ mis mascotas"
          title={
            <>
              Tus mascotas<br />
              <span className="nb-highlight">con QR.</span>
            </>
          }
          subtitle="Cada perfil genera un QR para el collar. Si la pierden, quien lo escanee ve los datos y te contacta."
          fill="urgent"
        />
      </div>

      <div className="flex items-center justify-end px-4 pt-3">
        <NbButton fill="ink" onClick={() => navigate('/my-pets/new')} className="px-4 py-2.5">
          <Plus size={14} /> Agregar mascota
        </NbButton>
      </div>

      {isError && (
        <div className="mx-4 mt-4 border-2 border-red-500 bg-red-50 p-3 text-sm text-red-700">
          <strong>Error:</strong> {error.message}
        </div>
      )}

      {pets && pets.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center">
          <Heart size={40} />
          <p className="font-display text-[22px] leading-tight">Aún no tenés mascotas.</p>
          <p className="max-w-xs text-[13px] font-medium">
            Creá el perfil de tu primera mascota y generá un código QR para su collar.
          </p>
          <NbButton fill="ink" onClick={() => navigate('/my-pets/new')} className="px-5 py-3">
            <Plus size={14} /> Crear primer perfil
          </NbButton>
        </div>
      )}

      {pets && pets.length > 0 && (
        <div className="grid gap-4 px-4 pt-4 md:grid-cols-2 lg:grid-cols-3">
          {pets.map((pet) => {
            const photo = pet.gallery?.[0];
            return (
              <NbCard key={pet.id} className="p-0">
                <div
                  className="relative h-40 w-full overflow-hidden"
                  style={{
                    borderBottom: '3px solid var(--nb-ink)',
                    background: '#e8e3d5',
                  }}
                >
                  {photo ? (
                    <img src={photo} alt={pet.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="nb-hatch h-full w-full" />
                  )}
                  <div className="absolute left-2 top-2">
                    <NbChip fill="paper">{ANIMAL_TYPES[pet.type]}</NbChip>
                  </div>
                </div>

                <div className="p-3">
                  <div className="font-display text-[22px] leading-none">{pet.name}</div>
                  <div
                    className="mt-1 text-[10px] font-bold uppercase tracking-[0.04em] opacity-80"
                    style={{ fontFamily: '"JetBrains Mono", monospace' }}
                  >
                    {ANIMAL_AGES[pet.age]} · {PET_GENDER[pet.gender]} ·{' '}
                    {PET_COLORS[pet.main_color] ?? pet.main_color}
                  </div>
                  {pet.race && (
                    <div className="mt-0.5 text-[12px] font-semibold">{pet.race}</div>
                  )}

                  {(pet.is_sterilized || pet.is_vaccinated || pet.is_microchipped) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {pet.is_sterilized && (
                        <NbChip fill="solved">
                          <Shield size={10} /> Esterilizado
                        </NbChip>
                      )}
                      {pet.is_vaccinated && (
                        <NbChip fill="found">
                          <Syringe size={10} /> Vacunado
                        </NbChip>
                      )}
                      {pet.is_microchipped && (
                        <NbChip fill="lost">
                          <Microchip size={10} /> Microchip
                        </NbChip>
                      )}
                    </div>
                  )}

                  {pet.medical_notes && (
                    <p className="mt-2 line-clamp-2 text-[11px] italic opacity-70">
                      "{pet.medical_notes}"
                    </p>
                  )}

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <NbButton fill="lost" size="sm">
                          <Download size={12} /> QR
                        </NbButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={() => handleDownloadQR(pet, 'png')}>
                          PNG {fileType === 'png' && '·'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownloadQR(pet, 'svg')}>
                          SVG {fileType === 'svg' && '·'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <NbButton
                      fill="found"
                      size="sm"
                      onClick={() => handleOpenPublicProfile(pet.uuid)}
                    >
                      <ExternalLink size={12} /> Ver
                    </NbButton>
                    <NbButton
                      fill="paper"
                      size="sm"
                      onClick={() => navigate(`/my-pets/${pet.id}/edit`)}
                    >
                      <Edit size={12} /> Editar
                    </NbButton>
                    <NbButton
                      fill="urgent"
                      size="sm"
                      onClick={() => handleDeletePet(pet.id, pet.name)}
                    >
                      <Trash2 size={12} /> Borrar
                    </NbButton>
                  </div>
                </div>
              </NbCard>
            );
          })}
        </div>
      )}

      {showScrollButton && (
        <button
          onClick={scrollToTop}
          aria-label="Volver al inicio"
          className="nb-btn fill-urgent fixed bottom-20 right-3 z-20 px-2 py-2"
        >
          <ArrowUpToLineIcon color="black" />
        </button>
      )}
    </div>
  );
};
